/* =============================================================
   assets/predictive-search.js  —  HandlerKraft (audited rewrite)

   Same public surface as Dawn's element (isOpen, open(), close(),
   closeResults(), getSearchResults()), so header.liquid's
   StickyHeader keeps working.

   Fixes vs. the old file:
   - TypeError that silently killed the search: updateSearchForTerm()
     built a RegExp from raw user text and dereferenced a null match
   - out-of-order responses: an old, slower request could overwrite
     newer results (only "reset" ever aborted a request)
   - unguarded DOM lookups that threw and left the panel stuck
   - Escape key support (Chrome also clears type=search on Escape)
   - keyboard: arrow keys repeat, selected row scrolls into view,
     "Search for ..." row now has an id for aria-activedescendant
   - duplicate option ids across the 3 instances (mobile modal,
     desktop header, search page)
   - one shared, size-capped cache instead of a copy per instance
   - panel no longer re-opens after the user already tabbed away
   - desktop panel stays inside the viewport and right of the catalog
     rail (replaces the hard-coded `right: -260px`, see --hk-ps-shift /
     --hk-ps-w in CSS)
   - list-item entrance animation only plays on first open, not on
     every keystroke

   UX-050 (search rework):
   - focus with an empty field opens the empty state (snippets/hk-search-zero:
     recent searches, popular searches, categories) instead of nothing
   - the inline desktop search dims the page while its panel is open
     (html.hk-search-open; the scrim is drawn by component-search.css)
   - recent searches: last 5 terms, localStorage, per visitor
   - a popular/recent term fills the field and shows the results in place
   ============================================================= */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.uid = ++PredictiveSearch.count;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    // the box that is shown/placed: a wrapper around the empty state + results in the header, else the results
    this.panel = this.querySelector('[data-predictive-search-panel]') || this.predictiveSearchResults;
    this.zero = this.querySelector('[data-hk-ps-zero]');
    this.host = this.closest('.desktop-search'); // inline desktop search: dims the page
    this.isOpen = false;
    this.abortController = new AbortController();
    this.requestId = 0;
    this.searchTerm = '';
    this.statusTimer = null;
    this.onResize = this.onResize.bind(this);

    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.abortPending();
    window.removeEventListener('resize', this.onResize);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    // after Escape the field keeps focus: a click on it opens the panel again
    this.input.addEventListener('click', () => {
      if (!this.isOpen) this.onFocus();
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onClick.bind(this));

    // The panel outlives a blur inside the search modal (onFocusOut): close it with the modal.
    const modalDetails = this.closest('details-modal details');
    if (modalDetails) {
      modalDetails.addEventListener('toggle', () => {
        if (!modalDetails.open && this.isOpen) this.close();
      });
    }

    // The scrim is the host's ::before (the whole viewport): wheel/touch on it must not scroll the page behind.
    if (this.host) {
      const stopScroll = (event) => {
        if (this.isOpen && event.target === this.host) event.preventDefault();
      };
      this.host.addEventListener('wheel', stopScroll, { passive: false });
      this.host.addEventListener('touchmove', stopScroll, { passive: false });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  /* ---------- typing ---------- */

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Results are no longer relevant for the new term: drop them so they
      // don't flash when the dropdown opens again.
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the "Search for ..." label right away, don't wait for the network.
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortPending();
      if (this.zero && this.contains(document.activeElement)) this.showZero();
      else this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    PredictiveSearch.rememberTerm(this.getQuery());
  }

  // Popular/recent terms fill the field and show the results here; a click on a result remembers the term.
  onClick(event) {
    const termLink = event.target.closest('[data-hk-ps-term]');
    if (termLink && this.zero && this.zero.contains(termLink)) {
      event.preventDefault();
      this.input.value = termLink.getAttribute('data-hk-ps-term');
      this.input.focus();
      this.onChange();
      return;
    }

    if (event.target.closest('[data-hk-ps-recent-clear]')) {
      PredictiveSearch.forgetTerms();
      this.renderRecent();
      this.input.focus();
      return;
    }

    if (event.target.closest('[data-predictive-search] a') && this.searchTerm) {
      PredictiveSearch.rememberTerm(this.searchTerm);
    }
  }

  // Scoped to THIS instance (the base class looks at the whole document, so a
  // highlighted row in another instance used to block the reset button).
  shouldResetForm() {
    return !this.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (this.shouldResetForm()) {
      this.searchTerm = '';
      this.abortPending();
      if (this.zero) this.showZero();
      else this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.zero) this.showZero();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Value was changed from another search input: treat it as a user change.
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      // In the open search modal the panel IS the sheet: closing it on blur (iOS "Done", a tap on the
      // bar) left a blank white screen. The modal's own ×, overlay and Escape close everything.
      if (this.inOpenModal()) return;
      this.close();
    });
  }

  inOpenModal() {
    const details = this.closest('details-modal details');
    return !!(details && details.hasAttribute('open'));
  }

  /* ---------- keyboard ---------- */

  onKeydown(event) {
    if (event.isComposing) return; // IME composition (RU/RO diacritics input methods)

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault(); // keep the caret where it is
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Escape':
        if (this.isOpen) {
          // Also stops Chrome from clearing <input type="search"> on Escape.
          event.preventDefault();
          this.close();
          this.input.focus();
        }
        break;
      case 'Enter': {
        const selected = this.getSelectedOption();
        if (selected) {
          event.preventDefault();
          selected.click();
        }
        break;
      }
    }
  }

  onKeyup(event) {
    if (this.getQuery().length || event.key === 'Escape') return;
    if (!this.zero) {
      this.close(true);
    } else if (!this.hasAttribute('data-zero') || !this.isOpen) {
      // field emptied (Backspace, Ctrl+A Delete): back to the empty state at once, not after the debounce
      this.abortPending();
      this.searchTerm = '';
      this.showZero();
    }
  }

  getSelectedOption() {
    return this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    // offsetParent filters out anything that is display:none
    const options = Array.from(this.querySelectorAll('[role="option"]')).filter((el) => el.offsetParent !== null);
    if (!options.length) return;

    const current = this.querySelector('[role="option"][aria-selected="true"]');
    let index = current ? options.indexOf(current) : -1;

    if (direction === 'up') {
      if (index === -1) return;
      index = index === 0 ? options.length - 1 : index - 1;
    } else {
      index = index === options.length - 1 ? 0 : index + 1;
    }

    const next = options[index];
    if (!next || next === current) return;

    if (this.statusElement) this.statusElement.textContent = '';
    if (current) current.setAttribute('aria-selected', 'false');
    next.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', next.id);
    next.scrollIntoView({ block: 'nearest' });
  }

  /* ---------- "Search for <term>" label ---------- */

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    const text = el?.textContent;
    if (!el || !text || !previousTerm) return;

    // Plain, case-insensitive substring swap. The old code did
    // new RegExp(previousTerm) (crashes on "(", "+", "[" ...) and then read
    // .length of a possibly-null match (crashes when the case differs from
    // the cached markup), which aborted onChange() before the fetch ran.
    const haystack = text.toLowerCase();
    const needle = previousTerm.toLowerCase();
    const at = haystack.indexOf(needle);
    if (at === -1 || haystack.indexOf(needle, at + 1) !== -1) return; // missing or ambiguous: leave as is

    el.textContent = text.slice(0, at) + newTerm + text.slice(at + previousTerm.length);
  }

  /* ---------- network ---------- */

  abortPending() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.requestId += 1; // invalidates anything still in flight
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();

    // Cancel the previous request no matter what, so a slow answer for
    // "for" can never overwrite the answer for "fork".
    this.abortPending();
    const requestId = this.requestId;
    const { signal } = this.abortController;

    this.setLiveRegionLoadingState();

    const cached = PredictiveSearch.cache.get(queryKey);
    if (cached) {
      this.renderSearchResults(cached.markup, cached.term);
      return;
    }

    const url =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&section_id=predictive-search&resources[limit]=10&resources[limit_scope]=each` +
      `&resources[type]=product,query,collection`;

    fetch(url, { signal })
      .then((response) => {
        if (response.status === 429) {
          const throttled = new Error('Predictive search throttled');
          throttled.throttled = true;
          throw throttled;
        }
        if (!response.ok) throw new Error(`Predictive search HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (requestId !== this.requestId) return; // a newer request took over

        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section missing in response');

        const markup = section.innerHTML;
        PredictiveSearch.remember(queryKey, markup, searchTerm);
        this.renderSearchResults(markup, searchTerm);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (requestId !== this.requestId) return;

        this.removeAttribute('loading');
        if (error?.throttled) return; // keep whatever is on screen
        if (this.zero && this.inOpenModal()) this.showZero();
        else this.close();
        console.warn('[predictive-search]', error);
      });
  }

  static remember(key, markup, term) {
    const cache = PredictiveSearch.cache;
    cache.delete(key);
    cache.set(key, { markup, term });
    while (cache.size > PredictiveSearch.CACHE_LIMIT) cache.delete(cache.keys().next().value);
  }

  /* ---------- rendering ---------- */

  renderSearchResults(resultsMarkup, renderedTerm) {
    this.clearSelection();
    this.removeAttribute('data-zero');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.makeOptionIdsUnique();
    const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (listbox) {
      listbox.id = `predictive-search-results-${this.uid}`;
      this.setPopup(listbox.id);
    }

    // Cached markup may carry the term with different casing.
    if (renderedTerm && renderedTerm !== this.searchTerm) {
      this.updateSearchForTerm(renderedTerm, this.searchTerm);
    }

    this.setAttribute('results', true);
    this.setLiveRegionResults();

    // Don't pop the dropdown open if the user already left the field
    // while the request was in flight.
    if (this.contains(document.activeElement)) this.open();
  }

  // The same section markup is injected into every instance on the page, so
  // option ids collide and aria-activedescendant pointed at the wrong copy.
  makeOptionIdsUnique() {
    this.predictiveSearchResults.querySelectorAll('[role="option"]').forEach((option, i) => {
      if (!option.id) option.id = `predictive-search-option-extra-${i + 1}`;
      option.id = `${option.id}-${this.uid}`;
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(count ? count.textContent : '');
  }

  /* ---------- empty state (UX-050) ---------- */

  showZero() {
    this.renderRecent();
    if (!this.hasAttribute('data-zero')) {
      this.clearSelection();
      this.setAttribute('data-zero', '');
    }
    this.setPopup(this.zero.id);
    this.removeAttribute('loading');
    this.open();
  }

  renderRecent() {
    const group = this.querySelector('[data-hk-ps-recent]');
    const list = group && group.querySelector('[data-hk-ps-recent-list]');
    const tpl = group && group.querySelector('template[data-hk-ps-recent-tpl]');
    if (!list || !tpl) return;

    const terms = PredictiveSearch.recentTerms();
    list.replaceChildren();
    terms.forEach((term, i) => {
      const row = tpl.content.firstElementChild.cloneNode(true);
      const link = row.querySelector('[data-hk-ps-term]');
      row.id = `hk-psz-${this.uid}-r${i + 1}`;
      link.setAttribute('data-hk-ps-term', term);
      link.href = `${link.getAttribute('href')}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      row.querySelector('[data-hk-ps-term-text]').textContent = term;
      list.appendChild(row);
    });
    group.hidden = terms.length === 0;
  }

  setPopup(id) {
    if (!id) return;
    this.input.setAttribute('aria-controls', id);
    this.input.setAttribute('aria-owns', id);
  }

  clearSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
    this.input.setAttribute('aria-activedescendant', '');
  }

  static recentKey() {
    return `${PredictiveSearch.RECENT_KEY}:${document.documentElement.lang || 'ro'}`;
  }

  static recentTerms() {
    try {
      const list = JSON.parse(localStorage.getItem(PredictiveSearch.recentKey()) || '[]');
      return Array.isArray(list)
        ? list.filter((t) => typeof t === 'string' && t.trim()).slice(0, PredictiveSearch.RECENT_LIMIT)
        : [];
    } catch (e) {
      return [];
    }
  }

  static rememberTerm(term) {
    const clean = String(term || '').trim().slice(0, 80);
    if (!clean) return;
    const lower = clean.toLowerCase();
    const list = [clean, ...PredictiveSearch.recentTerms().filter((t) => t.toLowerCase() !== lower)];
    try {
      localStorage.setItem(PredictiveSearch.recentKey(), JSON.stringify(list.slice(0, PredictiveSearch.RECENT_LIMIT)));
    } catch (e) {
      /* private mode / storage blocked: recent searches are only a convenience */
    }
  }

  static forgetTerms() {
    try {
      localStorage.removeItem(PredictiveSearch.recentKey());
    } catch (e) {
      /* ignore */
    }
  }

  /* ---------- open / close ---------- */

  open() {
    const wasOpen = this.isOpen;

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    if (!wasOpen) window.addEventListener('resize', this.onResize);

    if (this.host) document.documentElement.classList.add('hk-search-open');
    this.fitPanelToViewport();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.removeAttribute('data-zero');
    this.isOpen = false;
    window.removeEventListener('resize', this.onResize);
    if (this.host) document.documentElement.classList.remove('hk-search-open');
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
  }

  /* ---------- desktop header panel: placement ---------- */

  onResize() {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.fitPanelToViewport());
  }

  // Places the wide header panel. The old CSS pushed it 260px to the right of
  // the search box (`right: -260px`), so it grew into the free space on the
  // right of the header. That is the behaviour we keep, but:
  //   - it never runs past the right edge of the screen, and
  //   - it never covers the "Toate categoriile" rail on its left: if there is
  //     not enough room the panel gets narrower instead.
  // CSS reads --hk-ps-shift (how far the panel's right edge sits beyond the
  // search box) and --hk-ps-w (only set when the panel had to shrink).
  fitPanelToViewport() {
    const panel = this.panel;
    const host = this.host;
    if (!panel || !host) return;

    const OLD_OFFSET = 260; // where the panel used to sit relative to the box
    const MARGIN = 16; // gap to the screen edge
    const GAP = 12; // gap to the catalog rail
    const MIN_WIDTH = 480; // don't squeeze the panel below this

    panel.style.removeProperty('--hk-ps-w');
    panel.style.removeProperty('--hk-ps-shift');

    const viewport = document.documentElement.clientWidth;
    const natural = panel.offsetWidth; // width CSS wants (1200px / compact)
    const anchorRight = host.getBoundingClientRect().right; // CSS `right` is relative to this box

    let minLeft = MARGIN;
    const rails = document.querySelectorAll('.header__inline-menu .hk-topnav-li--catalog, .hk-hdr-cat, .hk-catsl[data-hk-catsl="1"] .hk-catstatic .hk-cat3__l1');
    rails.forEach((rail) => {
      const rect = rail.getBoundingClientRect();
      if (rect.width > 0 && rect.right < anchorRight) minLeft = Math.max(minLeft, rect.right + GAP);
    });

    const maxRight = viewport - MARGIN;
    let width = natural;
    if (width > maxRight - minLeft) width = Math.max(maxRight - minLeft, Math.min(natural, MIN_WIDTH));

    // Right edge: the old position, clamped so the panel stays right of the
    // catalog rail and inside the screen.
    const panelRight = Math.min(Math.max(anchorRight + OLD_OFFSET, minLeft + width), maxRight);

    if (width < natural) panel.style.setProperty('--hk-ps-w', `${Math.floor(width)}px`);
    panel.style.setProperty('--hk-ps-shift', `${Math.round(panelRight - anchorRight)}px`);
  }
}

PredictiveSearch.count = 0;
PredictiveSearch.CACHE_LIMIT = 40;
PredictiveSearch.cache = new Map(); // shared by every instance on the page
PredictiveSearch.RECENT_KEY = 'hk-recent-searches';
PredictiveSearch.RECENT_LIMIT = 5;

customElements.define('predictive-search', PredictiveSearch);